/**
 * OCI CIDR Calculator — React Standalone version
 *
 * Dynamic wizard-based CIDR calculator where users can configure:
 * - Number of VCNs (1-10)
 * - Number of subnets per VCN
 * - Base CIDR per VCN
 *
 * The calculator then generates appropriate CIDR blocks.
 */

import { parseCidr, splitCidr, formatIp, overlaps, isValidPrefix, formatNum } from './cidrUtils.js';

/**
 * Create a dynamic topology definition based on user configuration.
 *
 * @param {number} vcnCount - Number of VCNs (1-10)
 * @param {number[]} subnetCounts - Array of subnet counts per VCN
 * @param {string[]} baseCidrs - Array of base CIDRs per VCN
 * @returns {{ title: string, vcns: Array<{ label: string, baseCidr: string, subnets: Array<{ label: string }> }> }}
 */
export function createDynamicTopologyDef(vcnCount, subnetCounts, baseCidrs) {
  const vcns = [];
  for (let i = 0; i < vcnCount; i++) {
    const subnetCount = subnetCounts[i] || 0;
    const baseCidr = baseCidrs[i] || '10.0.0.0/16';
    const subnets = [];
    for (let j = 0; j < subnetCount; j++) {
      subnets.push({
        label: `Subnet ${j + 1}`
      });
    }
    vcns.push({
      label: `VCN ${i + 1}`,
      baseCidr: baseCidr,
      subnets: subnets
    });
  }
  return {
    title: `Custom Configuration — ${vcnCount} VCN${vcnCount > 1 ? 's' : ''}`,
    vcns: vcns
  };
}

/**
 * Return IP address statistics for a subnet CIDR string.
 * OCI reserves 3 addresses per subnet:
 *   • start     = network address
 *   • start + 1 = default router / VCN gateway (reserved by OCI)
 *   • end       = reserved by Oracle (broadcast equivalent)
 * Usable instance IPs: start+2 … end−1.
 *
 * @param {string} cidr - e.g. "10.0.1.0/24"
 * @returns {{
 *   total: number,
 *   usable: number,
 *   networkAddr: string,
 *   gatewayAddr: string,
 *   firstUsable: string,
 *   lastUsable: string,
 *   lastAddr: string,
 *   prefix: number
 * } | null}
 */
export function getSubnetInfo(cidr) {
  const p = parseCidr(cidr);
  if (!p) return null;
  const total = (p.end - p.start + 1) >>> 0;
  const usable = Math.max(0, total - 3);
  return {
    total,
    usable,
    prefix: p.prefix,
    networkAddr:  formatIp(p.start),
    gatewayAddr:  formatIp(p.start + 1),
    firstUsable:  formatIp(p.start + 2),
    lastUsable:   formatIp(p.end - 1),
    lastAddr:     formatIp(p.end),
  };
}

/**
 * Return VCN-level statistics given a VCN CIDR and the subnet prefix that
 * will be used within it.
 *
 * @param {string} vcnCidr - e.g. "10.0.0.0/16"
 * @param {number} subnetPrefix - e.g. 24
 * @returns {{ totalIps: number, subnetsPossible: number } | null}
 */
export function getVcnInfo(vcnCidr, subnetPrefix) {
  const p = parseCidr(vcnCidr);
  if (!p) return null;
  const totalIps = (p.end - p.start + 1) >>> 0;
  const subnetsPossible =
    subnetPrefix > p.prefix ? 1 << (subnetPrefix - p.prefix) : 0;
  return { totalIps, subnetsPossible };
}

/**
 * Choose the smallest k such that 2^k >= n (k >= 0).
 * @param {number} n
 * @returns {number}
 */
function ceilLog2(n) {
  if (n <= 1) return 0;
  let k = 0;
  while ((1 << k) < n) k++;
  return k;
}

/**
 * Determine the recommended subnet prefix for a given subnet slot count M
 * inside a VCN of prefix vcnPrefix. If the computed prefix does not fit M
 * subnets inside the VCN, it is incremented until it fits or reaches /29.
 *
 * Sizing table:
 *   M ≤  8  →  /24  (start)
 *   M ≤ 16  →  /25
 *   M ≤ 32  →  /26
 *   M > 32  →  /27
 *
 * @param {number} M - number of subnets needed
 * @param {number} vcnPrefix - prefix of the parent VCN block
 * @returns {{ prefix: number, ok: boolean }}
 */
function chooseSubnetPrefix(M, vcnPrefix) {
  let start;
  if      (M <= 8)  start = 24;
  else if (M <= 16) start = 25;
  else if (M <= 32) start = 26;
  else               start = 27;

  // Ensure chosen prefix is at least 1 more than vcnPrefix
  let prefix = Math.max(start, vcnPrefix + 1);

  // Increment until M subnets fit: need M <= 2^(prefix - vcnPrefix)
  while (prefix <= 29 && M > (1 << (prefix - vcnPrefix))) {
    prefix++;
  }

  const ok = M <= (1 << (prefix - vcnPrefix));
  return { prefix, ok };
}

/**
 * Calculate the required prefix to accommodate a given number of hosts.
 * OCI reserves 3 IPs per subnet (network, gateway, broadcast).
 * Formula: usable = 2^(32-prefix) - 3 >= requiredHosts
 *
 * @param {number} requiredHosts - number of usable host IPs needed
 * @returns {{ prefix: number, usable: number, total: number }}
 */
export function calculatePrefixForHosts(requiredHosts) {
  // Minimum usable hosts is 0 (for /30 and /31 which aren't valid for OCI subnets)
  // OCI subnets can be /30 max (2 usable IPs)
  if (requiredHosts <= 0) {
    return { prefix: 24, usable: 253, total: 256 };
  }

  // Find the smallest prefix where usable IPs >= requiredHosts
  // usable = 2^(32-prefix) - 3
  // 2^(32-prefix) >= requiredHosts + 3
  // 32 - prefix >= log2(requiredHosts + 3)
  // prefix <= 32 - log2(requiredHosts + 3)

  const neededTotal = requiredHosts + 3;
  const bits = Math.ceil(Math.log2(neededTotal));
  let prefix = 32 - bits;

  // OCI subnet constraints: /16 to /30
  if (prefix < 16) prefix = 16;
  if (prefix > 30) prefix = 30;

  const total = 1 << (32 - prefix);
  const usable = total - 3;

  return { prefix, usable, total };
}

/**
 * Calculate VLSM (Variable Length Subnet Masking) subnets for a VCN.
 * Each subnet gets a prefix based on its required host count.
 * Uses optimal allocation: sorts by size (largest first) to minimize fragmentation.
 *
 * @param {string} vcnCidr - VCN CIDR block
 * @param {Array<{ label: string, hosts: number }>} subnetDefs - subnet definitions with host counts
 * @returns {{ subnetCidrs: string[], error: string|null, warnings: string[] }}
 */
export function calculateVlsmSubnets(vcnCidr, subnetDefs) {
  const vcnParsed = parseCidr(vcnCidr);
  if (!vcnParsed) return { subnetCidrs: [], error: 'Invalid VCN CIDR.', warnings: [] };
  if (subnetDefs.length === 0) return { subnetCidrs: [], error: null, warnings: [] };

  const vcnSize = vcnParsed.end - vcnParsed.start + 1;
  const warnings = [];

  // Calculate required size for each subnet and sort by size (largest first)
  // This is the optimal VLSM allocation strategy to minimize fragmentation
  const subnetsWithSizes = subnetDefs.map((subnet, index) => {
    const { prefix, usable, total } = calculatePrefixForHosts(subnet.hosts || 0);
    const size = 1 << (32 - prefix);
    return {
      ...subnet,
      originalIndex: index,
      prefix,
      size,
      usable,
      total,
      insufficient: usable < (subnet.hosts || 0)
    };
  }).sort((a, b) => b.size - a.size); // Largest first

  // Check for subnets that requested more hosts than can be provided
  subnetsWithSizes.forEach(subnet => {
    if (subnet.insufficient) {
      warnings.push(`${subnet.label}: Requested ${subnet.hosts} hosts but /${subnet.prefix} only provides ${subnet.usable} usable IPs. Maximum per subnet is 65534 hosts.`);
    }
  });

  // Pre-calculate total space needed
  const totalNeeded = subnetsWithSizes.reduce((sum, s) => sum + s.size, 0);

  // Check if all subnets can fit within the VCN
  if (totalNeeded > vcnSize) {
    const totalHosts = subnetDefs.reduce((sum, s) => sum + (s.hosts || 0), 0);
    return {
      subnetCidrs: [],
      error: `Cannot fit all ${subnetDefs.length} subnets in ${vcnCidr}. ` +
             `Required: ${formatNum(totalNeeded)} IPs, ` +
             `Available: ${formatNum(vcnSize)} IPs. ` +
             `Consider using a larger base CIDR or reducing host counts.`,
      warnings
    };
  }

  // Allocate subnets sequentially (largest first)
  const subnetCidrs = new Array(subnetDefs.length);
  let currentStart = vcnParsed.start;

  for (const subnet of subnetsWithSizes) {
    // Double-check this subnet fits (should always fit if pre-check passed)
    if (currentStart + subnet.size > vcnParsed.end + 1) {
      return {
        subnetCidrs: [],
        error: `Cannot fit ${subnet.label} with ${subnet.hosts} hosts (requires /${subnet.prefix}) inside ${vcnCidr}. ` +
               `Available space: ${formatNum(vcnParsed.end + 1 - currentStart)} IPs`,
        warnings
      };
    }

    // Create CIDR for this subnet
    const subnetCidr = `${formatIp(currentStart)}/${subnet.prefix}`;
    subnetCidrs[subnet.originalIndex] = subnetCidr;

    currentStart += subnet.size;
  }

  // Check for wasted space (fragmentation)
  const wastedSpace = vcnSize - totalNeeded;
  const wastePercentage = (wastedSpace / vcnSize) * 100;
  if (wastePercentage > 25) {
    warnings.push(`High fragmentation: ${formatNum(wastedSpace)} IPs (${wastePercentage.toFixed(1)}%) of VCN space unused. Consider adjusting host counts for better utilization.`);
  }

  return { subnetCidrs, error: null, warnings };
}

/**
 * Calculate the minimum VCN CIDR that can accommodate equal-size subnets from a base CIDR.
 * Expands the base CIDR if needed to fit all subnets, or shrinks it to minimize waste.
 *
 * @param {string} baseCidr - The base CIDR to start from
 * @param {number} subnetCount - Number of subnets needed
 * @returns {{ vcnCidr: string, prefix: number, expanded: boolean, reason: string|null }}
 */
export function calculateSmartVcnCidrForEqualSize(baseCidr, subnetCount) {
  const baseParsed = parseCidr(baseCidr);
  if (!baseParsed) {
    return { vcnCidr: '', prefix: 0, expanded: false, reason: 'Invalid base CIDR' };
  }

  if (!subnetCount || subnetCount <= 0) {
    return { vcnCidr: baseCidr, prefix: baseParsed.prefix, expanded: false, reason: null };
  }

  // Determine what subnet prefix would be chosen for this count
  const { prefix: subnetPrefix } = chooseSubnetPrefix(subnetCount, baseParsed.prefix);

  // Calculate total IPs needed: subnetCount * size of each subnet
  const subnetSize = 1 << (32 - subnetPrefix);
  const totalIpsNeeded = subnetCount * subnetSize;

  // Check base CIDR size first
  const baseSize = 1 << (32 - baseParsed.prefix);

  // Calculate minimum VCN prefix that can accommodate total IPs needed
  // VCN must be able to contain all subnets: 2^(32 - vcnPrefix) >= totalIpsNeeded
  // Also, VCN prefix must be <= subnetPrefix - 1 (to contain subnets)
  let minVcnPrefix = 32 - Math.ceil(Math.log2(totalIpsNeeded));

  // VCN prefix must be at least 1 less than subnet prefix to contain the subnets
  // e.g., /24 subnets need at least a /23 VCN to contain them
  const minVcnPrefixForContainment = subnetPrefix - 1;
  minVcnPrefix = Math.min(minVcnPrefix, minVcnPrefixForContainment);

  // Determine final prefix based on whether we need to expand or optimize
  let finalPrefix;
  if (totalIpsNeeded > baseSize) {
    // Base CIDR is too small - need to EXPAND to a larger block (smaller prefix number)
    finalPrefix = minVcnPrefix;
  } else {
    // Base CIDR is sufficient - use the most specific (largest prefix number) that fits
    // This minimizes IP waste by using the smallest CIDR that fits the requirements
    // Clamp to valid VCN range (/16 to /30) when not expanding
    if (minVcnPrefix < 16) minVcnPrefix = 16;
    if (minVcnPrefix > 30) minVcnPrefix = 30;
    finalPrefix = Math.max(baseParsed.prefix, minVcnPrefix);
  }

  const vcnSize = 1 << (32 - finalPrefix);
  const vcnCidr = `${formatIp(baseParsed.start)}/${finalPrefix}`;

  if (totalIpsNeeded > baseSize) {
    // Base CIDR is too small - we expanded to a larger block
    return {
      vcnCidr,
      prefix: finalPrefix,
      expanded: true,
      reason: `Base ${baseCidr} (${baseSize} IPs) expanded to /${finalPrefix} (${vcnSize} IPs) to fit ${subnetCount} subnets requiring ${totalIpsNeeded} IPs`
    };
  }

  // Base CIDR is sufficient - use optimized (most specific) prefix
  const optimized = finalPrefix > baseParsed.prefix;
  return {
    vcnCidr,
    prefix: finalPrefix,
    expanded: false,
    reason: optimized
      ? `Base ${baseCidr} optimized to /${finalPrefix} (${vcnSize} IPs) for ${subnetCount} subnets, minimizing address waste`
      : null
  };
}

/**
 * Calculate the minimum VCN prefix required to accommodate a set of VLSM subnets.
 * Returns the smallest prefix (largest block) that can fit all subnets.
 *
 * @param {Array<{ hosts: number }>} hostRequirements - array of required host counts
 * @returns {{ minPrefix: number, totalIps: number, subnetPrefixes: number[], violatesMinVcnConstraint: boolean, error: string|null }}
 */
export function calculateMinVcnPrefixForVlsm(hostRequirements) {
  if (!hostRequirements || hostRequirements.length === 0) {
    return { minPrefix: 16, totalIps: 65536, subnetPrefixes: [], violatesMinVcnConstraint: false, error: null };
  }

  // Calculate prefix for each subnet
  const subnetPrefixes = hostRequirements.map(req => {
    const { prefix } = calculatePrefixForHosts(req.hosts || 0);
    return prefix;
  });

  // Calculate total IPs needed (sum of all subnet sizes)
  const totalIps = subnetPrefixes.reduce((sum, prefix) => {
    return sum + (1 << (32 - prefix));
  }, 0);

  // Find minimum VCN prefix that can accommodate total IPs
  // VCN prefix must be <= smallest subnet prefix (to contain it)
  const smallestSubnetPrefix = Math.min(...subnetPrefixes);

  // Calculate how many bits we need for all subnets combined
  // totalIps must fit within 2^(32 - vcnPrefix)
  // so: 32 - vcnPrefix >= log2(totalIps)
  // vcnPrefix <= 32 - log2(totalIps)
  let minPrefix = 32 - Math.ceil(Math.log2(totalIps));

  // Check if this violates OCI's /16 minimum VCN constraint
  // If minPrefix < 16, we would need a /15, /14, etc. which is not allowed
  const violatesMinVcnConstraint = minPrefix < 16;
  let error = null;
  if (violatesMinVcnConstraint) {
    error = `Total subnets require ${formatNum(totalIps)} IPs, which exceeds the maximum VCN size of /16 (${formatNum(65536)} IPs). ` +
            `Consider reducing the number of subnets or selecting smaller subnet sizes.`;
  }

  // VCN prefix cannot be larger (more specific) than any subnet prefix it contains
  // A /24 VCN cannot contain a /24 subnet (needs at least /23)
  // So minPrefix must be at most smallestSubnetPrefix - 1
  // But we don't enforce this here - let the caller decide based on base CIDR constraints

  return { minPrefix, totalIps, subnetPrefixes, violatesMinVcnConstraint, error };
}

/**
 * Calculate the minimum VCN CIDR that can accommodate VLSM subnets from a base CIDR.
 * Expands the base CIDR if needed to fit all subnets.
 *
 * @param {string} baseCidr - The base CIDR to start from
 * @param {Array<{ hosts: number }>} hostRequirements - array of required host counts
 * @returns {{ vcnCidr: string, prefix: number, expanded: boolean, reason: string|null, error: string|null }}
 */
export function calculateSmartVcnCidrForVlsm(baseCidr, hostRequirements) {
  const baseParsed = parseCidr(baseCidr);
  if (!baseParsed) {
    return { vcnCidr: '', prefix: 0, expanded: false, reason: 'Invalid base CIDR', error: null };
  }

  if (!hostRequirements || hostRequirements.length === 0) {
    return { vcnCidr: baseCidr, prefix: baseParsed.prefix, expanded: false, reason: null, error: null };
  }

  // Check if base CIDR can accommodate the required size
  const baseSize = 1 << (32 - baseParsed.prefix);

  // Calculate minimum prefix needed
  const { minPrefix, totalIps, violatesMinVcnConstraint, error: constraintError } = calculateMinVcnPrefixForVlsm(hostRequirements);

  // Check if the required VCN size violates OCI /16 minimum constraint
  if (violatesMinVcnConstraint) {
    return {
      vcnCidr: '',
      prefix: 0,
      expanded: false,
      reason: null,
      error: constraintError
    };
  }

  // Determine final prefix based on whether we need to expand or optimize
  let finalPrefix;
  if (totalIps > baseSize) {
    // Base CIDR is too small - need to EXPAND to a larger block (smaller prefix number)
    // Use the minimum prefix calculated to fit all subnets, but clamp to /16 minimum
    finalPrefix = Math.max(minPrefix, 16);
  } else {
    // Base CIDR is sufficient - use the most specific (largest prefix number) that fits
    // This minimizes IP waste by using the smallest CIDR that fits the requirements
    finalPrefix = Math.max(baseParsed.prefix, minPrefix);
  }

  // Ensure final prefix is within valid OCI VCN range (/16 to /30)
  if (finalPrefix < 16) finalPrefix = 16;
  if (finalPrefix > 30) finalPrefix = 30;

  // Calculate the optimized VCN CIDR using the final prefix
  const vcnCidr = `${formatIp(baseParsed.start)}/${finalPrefix}`;
  const vcnSize = 1 << (32 - finalPrefix);

  if (totalIps > baseSize) {
    // Base CIDR is too small - we expanded to a larger block
    return {
      vcnCidr,
      prefix: finalPrefix,
      expanded: true,
      reason: `Base ${baseCidr} (${baseSize} IPs) expanded to /${finalPrefix} (${vcnSize} IPs) to fit ${totalIps} IPs required by VLSM subnets`,
      error: null
    };
  }

  // Base CIDR is sufficient - use the optimized (most specific) prefix
  // This minimizes IP waste by using the smallest CIDR that fits the requirements
  const optimized = finalPrefix > baseParsed.prefix;
  return {
    vcnCidr,
    prefix: finalPrefix,
    expanded: false,
    reason: optimized
      ? `Base ${baseCidr} optimized to /${finalPrefix} (${vcnSize} IPs) to fit ${totalIps} IPs required by VLSM subnets, minimizing address waste`
      : null,
    error: null
  };
}

/**
 * Validate VLSM subnets can fit within a VCN without overlapping.
 * Returns detailed information about the allocation.
 *
 * @param {string} vcnCidr - VCN CIDR block
 * @param {Array<{ hosts: number }>} hostRequirements - array of required host counts
 * @returns {{ valid: boolean, reason: string|null, allocation: Array<{ prefix: number, size: number }>|null }}
 */
export function validateVlsmAllocation(vcnCidr, hostRequirements) {
  const vcnParsed = parseCidr(vcnCidr);
  if (!vcnParsed) {
    return { valid: false, reason: 'Invalid VCN CIDR', allocation: null };
  }

  const vcnSize = vcnParsed.end - vcnParsed.start + 1;

  // Calculate what each subnet needs
  const allocations = hostRequirements.map(req => {
    const { prefix, usable } = calculatePrefixForHosts(req.hosts || 0);
    const size = 1 << (32 - prefix);
    return { prefix, size, required: req.hosts || 0, usable };
  });

  // Sort by size descending (optimal VLSM packing)
  const sorted = [...allocations].sort((a, b) => b.size - a.size);

  // Check total space
  const totalNeeded = sorted.reduce((sum, a) => sum + a.size, 0);
  if (totalNeeded > vcnSize) {
    return {
      valid: false,
      reason: `Total required ${formatNum(totalNeeded)} IPs exceeds VCN capacity ${formatNum(vcnSize)} IPs`,
      allocation: allocations
    };
  }

  // Check for individual subnets that can't be satisfied
  for (let i = 0; i < allocations.length; i++) {
    if (allocations[i].usable < allocations[i].required) {
      return {
        valid: false,
        reason: `Subnet ${i + 1} requires ${allocations[i].required} hosts but /${allocations[i].prefix} only provides ${allocations[i].usable} usable IPs`,
        allocation: allocations
      };
    }
  }

  return { valid: true, reason: null, allocation: allocations };
}

/**
 * Calculate VCN CIDR for a single VCN from its base CIDR.
 * If multiple VCNs share the same base, the base is split.
 *
 * @param {string} baseCidr - Base CIDR for this VCN
 * @param {number} vcnIndex - Index of this VCN
 * @param {number} totalVcnsInBase - Total VCNs sharing this base
 * @returns {{ vcnCidr: string, error: string|null }}
 */
export function calculateVcnCidr(baseCidr, vcnIndex, totalVcnsInBase) {
  const parsed = parseCidr(baseCidr);
  if (!parsed) {
    return { vcnCidr: '', error: 'Invalid base CIDR format.' };
  }

  if (totalVcnsInBase === 1) {
    // Single VCN gets the whole base
    return { vcnCidr: baseCidr, error: null };
  }

  // Split base into equal blocks for all VCNs sharing it
  const k = ceilLog2(totalVcnsInBase);
  let vcnPrefix = parsed.prefix + k;

  // Ensure VCN prefix is at least /16 and at most /24
  if (vcnPrefix < 16) vcnPrefix = 16;
  if (vcnPrefix > 24) {
    return {
      vcnCidr: '',
      error: `Cannot fit ${totalVcnsInBase} VCNs in ${baseCidr} — would need prefix /${vcnPrefix}. Use a larger base CIDR.`
    };
  }

  const blocks = splitCidr(baseCidr, vcnPrefix, totalVcnsInBase);
  if (blocks.length < totalVcnsInBase) {
    return {
      vcnCidr: '',
      error: `Not enough space in ${baseCidr} for ${totalVcnsInBase} VCNs.`
    };
  }

  // Find which position this VCN is in for this base
  return { vcnCidr: blocks[vcnIndex], error: null };
}

/**
 * Run the deterministic CIDR proposal algorithm for a dynamic topology.
 * This version handles per-VCN base CIDRs.
 *
 * @param {{ title: string, vcns: Array<{ label: string, baseCidr: string, subnets: Array }> }} topoDef - topology definition
 * @returns {{
 *   plan: Array<{ vcnCidr: string, subnetCidrs: string[] }>,
 *   errors: Array<{ type: 'base'|'vcn', vcnIndex?: number, message: string }>
 * }}
 */
export function proposeCidrs(topoDef) {
  if (!topoDef || !topoDef.vcns) {
    return { plan: [], errors: [{ type: 'base', message: 'Invalid topology definition.' }] };
  }

  const vcns = topoDef.vcns;
  const vcnCount = vcns.length;

  if (vcnCount === 0) {
    return { plan: [], errors: [] };
  }

  // Group VCNs by their base CIDR
  const baseGroups = new Map(); // baseCidr -> array of vcn indices
  vcns.forEach((vcn, vi) => {
    const base = vcn.baseCidr || '10.0.0.0/16';
    if (!baseGroups.has(base)) {
      baseGroups.set(base, []);
    }
    baseGroups.get(base).push(vi);
  });

  // Calculate VCN CIDRs for each group
  const vcnCidrs = new Array(vcnCount);
  const errors = [];

  for (const [baseCidr, indices] of baseGroups) {
    const parsed = parseCidr(baseCidr);
    if (!parsed) {
      errors.push({
        type: 'base',
        message: `Invalid base CIDR format: ${baseCidr}`
      });
      continue;
    }

    const totalInBase = indices.length;

    for (let i = 0; i < indices.length; i++) {
      const vcnIndex = indices[i];
      const { vcnCidr, error } = calculateVcnCidr(baseCidr, i, totalInBase);

      if (error) {
        errors.push({
          type: 'vcn',
          vcnIndex: vcnIndex,
          message: `VCN ${vcnIndex + 1}: ${error}`
        });
      }

      vcnCidrs[vcnIndex] = vcnCidr;
    }
  }

  // Allocate subnets within each VCN
  const plan = [];

  for (let vi = 0; vi < vcns.length; vi++) {
    const vcnDef = vcns[vi];
    const vcnCidr = vcnCidrs[vi];
    const M = vcnDef.subnets.length;

    if (!vcnCidr) {
      plan.push({ vcnCidr: '', subnetCidrs: new Array(M).fill('') });
      continue;
    }

    if (M === 0) {
      plan.push({ vcnCidr, subnetCidrs: [] });
      continue;
    }

    const vcnParsed = parseCidr(vcnCidr);
    const { prefix: subnetPrefix, ok } = chooseSubnetPrefix(M, vcnParsed.prefix);

    if (!ok) {
      errors.push({
        type: 'vcn',
        vcnIndex: vi,
        message: `VCN ${vi + 1} (${vcnDef.label}): Cannot fit ${M} subnets even at /29 inside ${vcnCidr}.`
      });
      plan.push({ vcnCidr, subnetCidrs: new Array(M).fill('') });
      continue;
    }

    const subnetCidrs = splitCidr(vcnCidr, subnetPrefix, M);
    plan.push({ vcnCidr, subnetCidrs });
  }

  return { plan, errors };
}

/**
 * Propose subnet CIDRs for a single VCN given its CIDR and the number of
 * subnet slots required.
 *
 * @param {string} vcnCidr - VCN address block, e.g. "172.16.0.0/18"
 * @param {number} subnetCount - number of subnet slots to fill
 * @returns {{ subnetCidrs: string[], error: string|null }}
 */
export function proposeSubnetsForVcn(vcnCidr, subnetCount) {
  const p = parseCidr(vcnCidr);
  if (!p) return { subnetCidrs: [], error: 'Invalid VCN CIDR.' };
  if (subnetCount === 0) return { subnetCidrs: [], error: null };

  const { prefix: subnetPrefix, ok } = chooseSubnetPrefix(subnetCount, p.prefix);
  if (!ok) {
    return {
      subnetCidrs: [],
      error: `Cannot fit ${subnetCount} subnet${subnetCount > 1 ? 's' : ''} inside ${vcnCidr} (even at /29).`,
    };
  }

  const subnetCidrs = splitCidr(vcnCidr, subnetPrefix, subnetCount);
  if (subnetCidrs.length < subnetCount) {
    return {
      subnetCidrs,
      error: `Only ${subnetCidrs.length} of ${subnetCount} subnets fit inside ${vcnCidr}.`,
    };
  }

  return { subnetCidrs, error: null };
}

/**
 * Validate a complete calculator plan and return a flat error map.
 * Key format: "vcn-{vi}" for VCN CIDRs, "subnet-{vi}-{si}" for subnet CIDRs.
 *
 * @param {Array<{ vcnCidr: string, subnetCidrs: string[] }>} plan
 * @returns {Record<string, string>} errors map
 */
export function validateCalculatorPlan(plan) {
  const errors = {};

  const IPV4_CIDR_RE = /^\d{1,3}(\.\d{1,3}){3}\/\d{1,2}$/;
  // Allow full RFC1918 private address ranges (not just /16 blocks)
  const ALLOWED = [
    { start: 0x0a000000, end: 0x0affffff },   // 10.0.0.0/8
    { start: 0xac100000, end: 0xac1fffff },   // 172.16.0.0/12
    { start: 0xc0a80000, end: 0xc0a8ffff },   // 192.168.0.0/16
  ];

  function checkCidr(raw) {
    const s = (raw || '').trim();
    if (!s) return null;
    if (s.includes(':')) return 'IPv6 not supported.';
    if (!IPV4_CIDR_RE.test(s)) return 'Invalid format — use x.x.x.x/nn.';
    const p = parseCidr(s);
    if (!p) return 'Invalid CIDR.';
    if (p.prefix < 16 || p.prefix > 30) return `Prefix /${p.prefix} invalid — use /16 to /30.`;
    const inSupernet = ALLOWED.some(sn => p.start >= sn.start && p.end <= sn.end);
    if (!inSupernet) return 'Must be within RFC1918 ranges (10.0.0.0/8, 172.16.0.0/12, or 192.168.0.0/16).';
    return null;
  }

  function checkContainment(subnetCidr, vcnCidr) {
    const sp = parseCidr(subnetCidr);
    const vp = parseCidr(vcnCidr);
    if (!sp || !vp) return null;
    if (sp.start < vp.start || sp.end > vp.end) {
      return `Subnet CIDR must be within VCN CIDR (${vcnCidr}).`;
    }
    return null;
  }

  // Per-field format / supernet / prefix checks
  plan.forEach(({ vcnCidr, subnetCidrs }, vi) => {
    const vcnErr = checkCidr(vcnCidr);
    if (vcnErr) errors[`vcn-${vi}`] = vcnErr;

    subnetCidrs.forEach((sn, si) => {
      const snErr = checkCidr(sn);
      if (snErr) {
        errors[`subnet-${vi}-${si}`] = snErr;
        return;
      }
      if (!errors[`vcn-${vi}`] && (vcnCidr || '').trim()) {
        const containErr = checkContainment(sn, vcnCidr);
        if (containErr) errors[`subnet-${vi}-${si}`] = containErr;
      }
    });
  });

  // VCN-to-VCN overlap check
  const validVcns = plan
    .map((v, i) => ({ idx: i, cidr: (v.vcnCidr || '').trim() }))
    .filter(v => v.cidr && parseCidr(v.cidr) && !errors[`vcn-${v.idx}`]);

  for (let i = 0; i < validVcns.length; i++) {
    for (let j = i + 1; j < validVcns.length; j++) {
      if (overlaps(validVcns[i].cidr, validVcns[j].cidr)) {
        errors[`vcn-${validVcns[i].idx}`] =
          errors[`vcn-${validVcns[i].idx}`] ||
          `VCN CIDR overlaps with VCN ${validVcns[j].idx + 1} (${validVcns[j].cidr}).`;
        errors[`vcn-${validVcns[j].idx}`] =
          errors[`vcn-${validVcns[j].idx}`] ||
          `VCN CIDR overlaps with VCN ${validVcns[i].idx + 1} (${validVcns[i].cidr}).`;
      }
    }
  }

  // Duplicate CIDR check
  const seen = new Map();
  plan.forEach(({ vcnCidr, subnetCidrs }, vi) => {
    const fields = [
      { key: `vcn-${vi}`, raw: vcnCidr },
      ...subnetCidrs.map((s, si) => ({ key: `subnet-${vi}-${si}`, raw: s })),
    ];
    fields.forEach(({ key, raw }) => {
      const s = (raw || '').trim();
      if (!s) return;
      const p = parseCidr(s);
      if (!p) return;
      const norm = `${formatIp(p.start)}/${p.prefix}`;
      if (seen.has(norm)) {
        const prev = seen.get(norm);
        errors[key] = errors[key] || 'Duplicate CIDR — already used in this plan.';
        errors[prev] = errors[prev] || 'Duplicate CIDR — already used in this plan.';
      } else {
        seen.set(norm, key);
      }
    });
  });

  return errors;
}

/**
 * Format a plan as human-readable text.
 * @param {Array<{ vcnCidr: string, subnetCidrs: string[] }>} plan
 * @param {{ title: string, vcns: Array<{ label: string, baseCidr: string, subnets: Array<{ label: string }> }> }} topoDef
 * @returns {string}
 */
export function planToText(plan, topoDef) {
  const lines = [`OCI CIDR Plan — ${topoDef.title}`, ''];
  topoDef.vcns.forEach((vcn, vi) => {
    const vp = plan[vi];
    lines.push(`${vcn.label} (Base: ${vcn.baseCidr})`);
    lines.push(`  VCN CIDR: ${vp?.vcnCidr || '(empty)'}`);
    vcn.subnets.forEach((s, si) => {
      lines.push(`  ${s.label}: ${vp?.subnetCidrs[si] || '(empty)'}`);
    });
    lines.push('');
  });
  return lines.join('\n');
}

/**
 * Format a plan as JSON.
 * @param {Array<{ vcnCidr: string, subnetCidrs: string[] }>} plan
 * @param {{ title: string, vcns: Array<{ label: string, baseCidr: string, subnets: Array<{ label: string }> }> }} topoDef
 * @returns {string}
 */
export function planToJson(plan, topoDef) {
  const map = {};
  topoDef.vcns.forEach((vcn, vi) => {
    const vcnPlan = plan[vi];
    if (!vcnPlan) return;
    map[`vcn${vi + 1}`] = {
      label: vcn.label,
      baseCidr: vcn.baseCidr,
      vcnCidr: vcnPlan.vcnCidr,
      subnets: vcn.subnets.map((s, si) => ({
        label: s.label,
        cidr: vcnPlan.subnetCidrs[si] || ''
      }))
    };
  });
  return JSON.stringify(map, null, 2);
}
